/**
 * ApplicationSankey — ECharts Sankey diagram of application state transitions.
 *
 * Nodes are colored by outcome category:
 *   neutral  — the virtual "Applications" source
 *   active   — in-progress stages (applied, phone_screen, interview, technical_interview)
 *   positive — offer / accepted
 *   negative — rejected, ghosted, withdrawn, declined
 *
 * The chart is rendered into a canvas via ECharts (tree-shaken imports).
 * It resizes automatically with a ResizeObserver.
 */
import { useEffect, useRef } from 'react';
import * as echarts from 'echarts/core';
import { SankeyChart } from 'echarts/charts';
import { TooltipComponent } from 'echarts/components';
import { SVGRenderer } from 'echarts/renderers';
import type { ApplicationSankeyData } from '@/lib/api';
import { APPLICATION_ACTION_LABELS } from '@/lib/api';
import type { ApplicationAction } from '@/lib/api';

echarts.use([SankeyChart, TooltipComponent, SVGRenderer]);

// ── Color palette ──────────────────────────────────────────────────────────────

const COLORS: Record<string, string> = {
  applied:              '#60a5fa', // blue-400
  phone_screen:         '#a78bfa', // violet-400
  interview:            '#c084fc', // purple-400
  technical_interview:  '#e879f9', // fuchsia-400
  offer:                '#34d399', // emerald-400
  accepted:             '#16a34a', // green-600
  rejected:             '#f87171', // red-400
  ghosted:              '#94a3b8', // slate-400
  withdrawn:            '#fb923c', // orange-400
  declined:             '#fb923c', // orange-400
  'Pending Response':   '#cbd5e1', // slate-300 — awaiting employer reply
};

function nodeColor(name: string): string {
  return COLORS[name] ?? '#94a3b8';
}

function nodeLabel(name: string): string {
  if (name === 'Applications') return 'Applications';
  return APPLICATION_ACTION_LABELS[name as ApplicationAction] ?? name;
}

// ── Build ECharts option ───────────────────────────────────────────────────────

function buildOption(data: ApplicationSankeyData) {
  // Collect unique node names from links
  const nameSet = new Set<string>();
  for (const link of data.links) {
    nameSet.add(link.source);
    nameSet.add(link.target);
  }

  const nodes = Array.from(nameSet).map((name) => ({
    name,
    itemStyle: { color: nodeColor(name) },
    label:     { formatter: () => nodeLabel(name) },
  }));

  const links = data.links.map((l) => ({
    source: l.source,
    target: l.target,
    value:  l.value,
  }));

  return {
    tooltip: {
      trigger: 'item',
      triggerOn: 'mousemove',
      formatter: (params: { dataType: string; data: { source: string; target: string; value: number; name: string } }) => {
        if (params.dataType === 'edge') {
          const { source, target, value } = params.data;
          const from = nodeLabel(source);
          const to   = nodeLabel(target);
          const label = value === 1 ? '1 application' : `${value} applications`;
          return `${from} → ${to}<br/><b>${label}</b>`;
        }
        // Node
        const name = params.data.name;
        const inflow  = links.filter((l) => l.target === name).reduce((s, l) => s + l.value, 0);
        const outflow = links.filter((l) => l.source === name).reduce((s, l) => s + l.value, 0);
        const count   = Math.max(inflow, outflow);
        return `<b>${nodeLabel(name)}</b><br/>${count} ${count === 1 ? 'application' : 'applications'}`;
      },
    },
    series: [
      {
        type: 'sankey',
        data: nodes,
        links,
        orient: 'horizontal',
        nodeAlign: 'left',
        nodeGap: 12,
        nodeWidth: 14,
        layoutIterations: 32,
        emphasis: { focus: 'adjacency' },
        label: {
          show: true,
          position: 'right',
          fontSize: 12,
          color: '#475569', // slate-600
          formatter: (params: { data: { name: string } }) => nodeLabel(params.data.name),
        },
        lineStyle: {
          color: 'gradient',
          opacity: 0.4,
          curveness: 0.5,
        },
      },
    ],
  };
}

// ── Component ──────────────────────────────────────────────────────────────────

interface ApplicationSankeyProps {
  data: ApplicationSankeyData;
  height?: number;
}

export function ApplicationSankey({ data, height = 280 }: ApplicationSankeyProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef     = useRef<echarts.ECharts | null>(null);

  // Init chart once
  useEffect(() => {
    if (!containerRef.current) return;
    const chart = echarts.init(containerRef.current, undefined, { renderer: 'svg' });
    chartRef.current = chart;

    const ro = new ResizeObserver(() => chart.resize());
    ro.observe(containerRef.current);

    return () => {
      ro.disconnect();
      chart.dispose();
      chartRef.current = null;
    };
  }, []);

  // Update option whenever data changes
  useEffect(() => {
    if (!chartRef.current || data.links.length === 0) return;
    chartRef.current.setOption(buildOption(data), { notMerge: true });
  }, [data]);

  if (data.links.length === 0) {
    return (
      <div
        className="flex items-center justify-center rounded-lg border border-dashed text-sm text-muted-foreground"
        style={{ height }}
      >
        No transition data yet — add applications and update their status to see the flow.
      </div>
    );
  }

  return <div ref={containerRef} style={{ width: '100%', height }} />;
}
